import {
  usePropertiesWithMedia,
  useSurveyConfig,
} from "@hooks/useSurveySelectors";
import PDFPreview from "@survey-preview/PDFPreview";

interface PropertyFlyerPreviewProps {
  propertyFilter?: string;
}

function PropertyFlyerPreview({ propertyFilter }: PropertyFlyerPreviewProps) {
  // Use combined hooks for optimized data access
  const surveyConfig = useSurveyConfig();
  const { visibleProperties: allVisibleProperties, mediaFiles } =
    usePropertiesWithMedia();

  // Filter properties if propertyFilter is provided
  const visibleProperties = propertyFilter
    ? allVisibleProperties.filter((prop) => prop.propertyId === propertyFilter)
    : allVisibleProperties;

  if (!surveyConfig.isIncludeFlyers || !mediaFiles) return null;

  // Collect all flyers for all visible properties
  const allFlyers: Array<{
    cloudfrontUrl: string;
    thumbnailUrl?: string;
    mediaId: string;
  }> = [];
  visibleProperties.forEach((property) => {
    const propertyMedia = mediaFiles[property.propertyId];
    if (!propertyMedia?.fliers) return;

    // Filter out source PDFs that have been modified (only show modified versions)
    const allPropertyFliers = propertyMedia.fliers;
    const modifiedMediaIds = new Set(
      allPropertyFliers
        .filter((file) => file.modifiedFromMediaId)
        .map((file) => file.modifiedFromMediaId),
    );

    const visibleFlyers = allPropertyFliers.filter(
      (flyer) =>
        !flyer.isHidden &&
        flyer.cloudfrontUrl &&
        !modifiedMediaIds.has(flyer.mediaId), // Hide source PDFs
    );

    if (visibleFlyers.length === 0) return;
    visibleFlyers.forEach((flyer) => {
      allFlyers.push({
        cloudfrontUrl: flyer.cloudfrontUrl!,
        thumbnailUrl: flyer.thumbnailUrl,
        mediaId: flyer.mediaId,
      });
    });
  });

  if (allFlyers.length === 0) return null;

  return (
    <>
      {allFlyers.map((flyer, index) => (
        <PDFPreview
          items={[flyer]}
          pdfPaperSize={surveyConfig.pdfPaperSize}
          key={`flyer-${flyer.mediaId || index}`}
        />
      ))}
    </>
  );
}

export default PropertyFlyerPreview;
